import styles from './section-14-tab.module.scss';
import {
  ChoiceChip,
  ChoiceChipGroup,
  Dropdown,
  Icon,
  ICONS_ENUM,
  IDropdownItem,
  Pill,
  Search,
  Typography
} from '@sanlam-glacier/app-common/components';
import { Section14Table } from '../../molecules/section-14-table/Section14Table';
import { useCallback, useEffect, useState } from 'react';
import { getClientsWithSection14RenewalsDue } from 'bff/hooks/my-practice/Section14FeeRenewalsApi';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  Section14NegativeJourneyCategory,
  Section14UrgencyTab
} from '../../../types/section-14/section-14.enums';
import { clsx } from 'clsx';
import { Section14ClientData } from 'modules/my-practice/types/section-14/section-14.interfaces';
import { ScreenSize, useScreenSize } from 'modules/case-tracking/hooks/useScreenSize';
import { debounce } from 'lodash';
import { Section14CardGrid } from '../../molecules/section-14-cards-view/Section14CardGrid';
import { formatSection14Clients } from 'modules/my-practice/utils/section-14/client-formatter';
import { matchesSearchCriteria } from 'modules/my-practice/utils/section-14/search-helpers';
import {
  getRenewalStatusFilterRange,
  renewalStatusContentReplacer
} from 'modules/my-practice/utils/section-14/status-filter-helpers';
import { Section14NoResults } from '../../molecules/section-14-no-results/Section14NoResults';
import { Section14ClientRenewalModel } from 'bff';
import ExcelDownloadButton from 'components/atoms/excel-download-button/ExcelDownloadButton';

type Section14TabProps = {
  onCountChange: (count: number) => void;
  onFilteredDataChange?: (data: Section14ClientRenewalModel[]) => void;
};
const Section14Tab = ({ onCountChange, onFilteredDataChange }: Section14TabProps) => {
  const testComponentName = 'Section14Tab';
  const [searchTerm, setSearchTerm] = useState('');
  const [negativeJourneyType, setNegativeJourneyType] =
    useState<Section14NegativeJourneyCategory>();
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<Section14UrgencyTab>(Section14UrgencyTab.upcoming);

  const getTitleText = () => {
    switch (activeTab) {
      case Section14UrgencyTab.all:
        return 'Fee renewals';
      case Section14UrgencyTab.upcoming:
        return 'Fee renewals (next 12 months)';
      case Section14UrgencyTab.late:
        return 'Fee renewals (past 12 months)';
      default:
        return 'Fee renewals';
    }
  };

  const [section14Clients, setSection14Clients] = useState<Section14ClientData>({
    [Section14UrgencyTab.all]: [],
    [Section14UrgencyTab.upcoming]: [],
    [Section14UrgencyTab.late]: []
  });
  const [selectedRenewalStatusFiltersStates, setSelectedRenewalStatusFiltersStates] = useState<{
    [Section14UrgencyTab.all]: (string | IDropdownItem)[];
    [Section14UrgencyTab.upcoming]: (string | IDropdownItem)[];
    [Section14UrgencyTab.late]: (string | IDropdownItem)[];
  }>({
    [Section14UrgencyTab.all]: [],
    [Section14UrgencyTab.upcoming]: [],
    [Section14UrgencyTab.late]: []
  });
  const [paginationStates, setPaginationStates] = useState({
    [Section14UrgencyTab.all]: 0,
    [Section14UrgencyTab.upcoming]: 0,
    [Section14UrgencyTab.late]: 0
  });
  const screenSize = useScreenSize();
  const isTablet = screenSize === ScreenSize.Tablet_md || screenSize === ScreenSize.Tablet_sm;

  const { data: clients } = useSuspenseQuery(getClientsWithSection14RenewalsDue());

  const getFilteredData = useCallback(
    (searchTerm: string, section14ClientData: Section14ClientData): Section14ClientData => {
      let results: Section14ClientData = {
        all: [],
        upcoming: [],
        late: []
      };

      if (!searchTerm || searchTerm.trim() === '') {
        results = section14ClientData;
      } else {
        if (Section14UrgencyTab.all) {
          results.all = section14ClientData.all
            .map((client) => matchesSearchCriteria(client, searchTerm))
            .filter(Boolean) as (typeof section14ClientData)[typeof activeTab];
        }
        if (Section14UrgencyTab.upcoming) {
          results.upcoming = section14ClientData.upcoming
            .map((client) => matchesSearchCriteria(client, searchTerm))
            .filter(Boolean) as (typeof section14ClientData)[typeof activeTab];
        }
        if (Section14UrgencyTab.late) {
          results.late = section14ClientData.late
            .map((client) => matchesSearchCriteria(client, searchTerm))
            .filter(Boolean) as (typeof section14ClientData)[typeof activeTab];
        }

        if (
          results.all.length === 0 &&
          results.upcoming.length === 0 &&
          results.late.length === 0
        ) {
          setNegativeJourneyType(Section14NegativeJourneyCategory.search);
          return results;
        }
      }

      let selectedFiltersPresent = false;

      if (
        activeTab !== Section14UrgencyTab.late &&
        selectedRenewalStatusFiltersStates[activeTab].length > 0
      ) {
        selectedFiltersPresent = true;
        const selectedFilters = selectedRenewalStatusFiltersStates[activeTab] as IDropdownItem[];
        const valuesOfSelectedStatusFliters = [
          ...new Set(selectedFilters.map((filter) => Number(filter.value)))
        ];

        const sortedValues = valuesOfSelectedStatusFliters.slice().sort((a, b) => a - b);

        const valueRanges = getRenewalStatusFilterRange(sortedValues);

        const filterSection14Clients = results[activeTab]
          .map((client) => {
            const filteredInvestments = client.investments.filter((inv) =>
              valueRanges.some(({ min, max }) => inv.daysUntilDue >= min && inv.daysUntilDue < max)
            );

            if (filteredInvestments.length > 0) {
              return { ...client, investments: filteredInvestments };
            }

            return null;
          })
          .filter(Boolean) as (typeof section14ClientData)[typeof activeTab];

        results = {
          ...results,
          [activeTab]: filterSection14Clients
        };
      }

      if (
        selectedFiltersPresent &&
        (results.all.length === 0 || results.upcoming.length === 0 || results.late.length === 0)
      ) {
        const selectedFilters = selectedRenewalStatusFiltersStates[activeTab] as IDropdownItem[];
        const contentOfSelectedStatusFliters = [
          ...new Set(selectedFilters.map((filter) => filter.content))
        ];
        setNegativeJourneyType(Section14NegativeJourneyCategory.filter);
        setSelectedFilters(contentOfSelectedStatusFliters);
      }
      setPaginationStates((prev) => ({
        ...prev,
        [activeTab]: 0
      }));

      return results;
    },
    [activeTab, selectedRenewalStatusFiltersStates]
  );

  useEffect(() => {
    if (!clients) return;
    const formattedData = formatSection14Clients(clients);
    setSection14Clients(formattedData);
    onCountChange(
      formattedData.all.reduce((total, client) => total + client.investments.length, 0)
    );
    const filteredData = getFilteredData(searchTerm, formattedData);
    setSection14Clients(filteredData);
    setFilteredClients(filteredData[activeTab]);
    if (onFilteredDataChange) {
      onFilteredDataChange(filteredData[activeTab]);
    }
  }, [clients, searchTerm, getFilteredData, onCountChange, activeTab, onFilteredDataChange]);

  const debouncedHandleSearch = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
    }, 500),
    []
  );

  const handleChoiceChipSelection = (
    _e: React.SyntheticEvent,
    value: string | string[] | undefined
  ) => {
    const selectedValue = value?.toString();
    if (
      selectedValue &&
      Object.values(Section14UrgencyTab).includes(selectedValue as Section14UrgencyTab)
    ) {
      setActiveTab(selectedValue as Section14UrgencyTab);
    }
  };

  const handleRenewalStatusFilterSelection = (
    value: string | IDropdownItem | (string | IDropdownItem)[]
  ) => {
    if (Array.isArray(value)) {
      const selectedItems = value as IDropdownItem[];

      if (selectedItems.length === 0) {
        if (selectedRenewalStatusFiltersStates[activeTab].length > 0) {
          setSelectedRenewalStatusFiltersStates((prev) => ({
            ...prev,
            [activeTab]: []
          }));
        }
        return;
      }

      setSelectedRenewalStatusFiltersStates((prev) => {
        const updatedFilters = selectedItems.map((item) =>
          item.content.startsWith('Due in')
            ? {
                ...item,
                content: renewalStatusContentReplacer(item.content)
              }
            : item
        );

        const valueChanged =
          prev[activeTab].length !== updatedFilters.length ||
          prev[activeTab].some((item, i) => item !== updatedFilters[i]);

        return valueChanged ? { ...prev, [activeTab]: updatedFilters } : prev;
      });
    }
  };

  const renewalStatusFilters: IDropdownItem[] = [
    {
      content: 'Due in 7 days or less',
      value: '7',
      pill: {
        children: 'High urgency',
        size: Pill.SIZE.SMALL,
        variant: Pill.VARIANT.SECONDARY,
        className: clsx(styles.highUrgency)
      }
    },
    {
      content: 'Due in 30 days or less',
      value: '30',
      pill: {
        children: 'Medium urgency',
        size: Pill.SIZE.SMALL,
        variant: Pill.VARIANT.SECONDARY,
        className: clsx(styles.mediumUrgency)
      }
    },
    {
      content: 'Due in 31 days or more',
      value: '31',
      pill: {
        children: 'Low urgency',
        size: Pill.SIZE.SMALL,
        variant: Pill.VARIANT.SECONDARY,
        className: clsx(styles.lowUrgency)
      }
    }
  ];

  const [filteredClients, setFilteredClients] = useState<Section14ClientRenewalModel[]>([]);

  const mapClientsToExcelRows = (clients: Section14ClientRenewalModel[]) =>
    clients.flatMap((client) =>
      client.investments.map((inv) => ({
        Client: client.clientName,
        InvestmentNumber: inv.investmentNumber,
        InvestmentValue: inv.investmentValue,
        IntermediaryFee: inv.intermediaryFee,
        FeeRenewalDate: inv.feeRenewalDate,
        Status: inv.renewalStatus
      }))
    );

  return (
    <>
      <div className={styles.sectionHeading}>
        <div className={styles.titleAndChoiceChips}>
          <Typography
            variant={Typography.VARIANT.H5}
            weight={Typography.WEIGHT.MEDIUM}
            color={Typography.COLOR.INHERIT}
            className={styles.titleText}
          >
            {getTitleText()}
          </Typography>
          <ChoiceChipGroup
            defaultSelected={Section14UrgencyTab.upcoming}
            onChange={handleChoiceChipSelection}
            className={styles.choiceChips}
          >
            <ChoiceChip
              aria-label='All'
              value={Section14UrgencyTab.all}
              displayPill
              pillLabel={section14Clients[Section14UrgencyTab.all].reduce(
                (total, client) => total + client.investments.length,
                0
              )}
              inputProps={{ id: `${testComponentName}AllChip` }}
              pillProps={{ id: `${testComponentName}AllTabCount` }}
            >
              {`All`}
            </ChoiceChip>
            <ChoiceChip
              aria-label='Upcoming'
              value={Section14UrgencyTab.upcoming}
              displayPill
              pillLabel={section14Clients[Section14UrgencyTab.upcoming].reduce(
                (total, client) => total + client.investments.length,
                0
              )}
              inputProps={{ id: `${testComponentName}UpcomingChip` }}
              pillProps={{ id: `${testComponentName}UpcomingTabCount` }}
            >
              {`Upcoming`}
            </ChoiceChip>
            <ChoiceChip
              aria-label='Late'
              value={Section14UrgencyTab.late}
              displayPill
              pillLabel={section14Clients[Section14UrgencyTab.late].reduce(
                (total, client) => total + client.investments.length,
                0
              )}
              inputProps={{ id: `${testComponentName}LateChip` }}
              pillProps={{ id: `${testComponentName}LateTabCount` }}
            >
              {`Late`}
            </ChoiceChip>
          </ChoiceChipGroup>
        </div>
        <div className={styles.searchAndStatusFilter}>
          <Search
            size={Search.SIZE.SMALL}
            className={styles.searchBar}
            value={searchTerm}
            onChange={debouncedHandleSearch}
            placeholder='Type in client name or investment number...'
          />
          {activeTab !== Section14UrgencyTab.late ? (
            <div className={styles.renewalStatusFilterContainer}>
              <Icon className={styles.filterIcon} icon={ICONS_ENUM.CONTENT_FILTER_LIST} />
              <Dropdown
                data-testid={`${testComponentName}StatusRenewalFilterDropdown`}
                multiSelect={true}
                placeholder={
                  selectedRenewalStatusFiltersStates[activeTab].length === 0 ? 'Status' : undefined
                }
                onChange={handleRenewalStatusFilterSelection}
                popperPlacement={Dropdown.POSITION.BOTTOM}
                chipLimit={2}
                items={renewalStatusFilters}
                className={styles.renewalStatusFilterDropDown}
                selected={selectedRenewalStatusFiltersStates[activeTab]}
                inputVariant={Dropdown.VARIANT.TABLE}
                maxHeight={208}
                iconButtonProps={{ id: `${testComponentName}StatusRenewalFilterDropdownButton` }}
                inputProps={{ id: `${testComponentName}StatusRenewalFilterDropdownPlaceholder` }}
                pureFormInputProps={{
                  id: `${testComponentName}StatusRenewalFilterDropdownFormInput`
                }}
              />
              <ExcelDownloadButton
                data={mapClientsToExcelRows(filteredClients)}
                fileName='section_14_fee_renewal'
              />
            </div>
          ) : (
            <div className={styles.excelButtonWrapper}>
              <ExcelDownloadButton
                data={mapClientsToExcelRows(filteredClients)}
                fileName='section_14_fee_renewal'
              />
            </div>
          )}
        </div>
      </div>
      {section14Clients[activeTab].length === 0 ? (
        <Section14NoResults
          searchTerm={searchTerm}
          selectedFilters={selectedFilters}
          negativeJourneyType={negativeJourneyType}
        />
      ) : (
        <>
          {isTablet ? (
            <Section14CardGrid
              currentPage={paginationStates[activeTab]}
              setActiveTabCurrentPage={(page: number) =>
                setPaginationStates((prev) => ({
                  ...prev,
                  [activeTab]: page
                }))
              }
              clients={section14Clients[activeTab]}
              activeTab={activeTab}
            />
          ) : (
            <Section14Table
              currentPage={paginationStates[activeTab]}
              setActiveTabCurrentPage={(page: number) =>
                setPaginationStates((prev) => ({
                  ...prev,
                  [activeTab]: page
                }))
              }
              clients={section14Clients[activeTab]}
              activeTab={activeTab}
            />
          )}
        </>
      )}
    </>
  );
};

export default Section14Tab;
